import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { LogOut, Plus, Edit, Trash2, Video, FileText, Settings } from 'lucide-react';
import SocialSettingsPanel from '../components/SocialSettingsPanel';
import AdsSettingsPanel from '../components/AdsSettingsPanel';
import LegalSettingsPanel from '../components/LegalSettingsPanel';
import ChangePasswordPanel from '../components/ChangePasswordPanel';
import SEO from '../components/SEO';
import { getArticleSnippet } from '../utils/articleHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AdminDashboardPage() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [videos, setVideos] = useState([]);
  const [activeTab, setActiveTab] = useState('articles');

  useEffect(() => {
    if (!loading && (!user || user.role !== 'admin')) {
      navigate('/admin');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchArticles();
      fetchVideos();
    }
  }, [user]);

  const fetchArticles = async () => {
    try {
      const { data } = await axios.get(`${API}/articles`);
      setArticles(data);
    } catch (error) {
      console.error('Error fetching articles:', error);
    }
  };

  const fetchVideos = async () => {
    try {
      const { data } = await axios.get(`${API}/videos`);
      setVideos(data);
    } catch (error) {
      console.error('Error fetching videos:', error);
    }
  };

  const handleDeleteArticle = async (id) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    
    try {
      await axios.delete(`${API}/articles/${id}`, { withCredentials: true });
      fetchArticles();
    } catch (error) {
      console.error('Error deleting article:', error);
      alert('Failed to delete article');
    }
  };

  const handleDeleteVideo = async (id) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    
    try {
      await axios.delete(`${API}/videos/${id}`, { withCredentials: true });
      fetchVideos();
    } catch (error) {
      console.error('Error deleting video:', error);
      alert('Failed to delete video');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin');
  };

  if (loading) {
    return <div className="min-h-screen bg-[#FFF8F0] flex items-center justify-center">Loading...</div>;
  }

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FFF8F0]" data-testid="admin-dashboard">
      <SEO title="Admin Dashboard" noindex />
      <div className="bg-white border-b border-[#E5E5E5] p-6">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between">
          <h1 className="text-3xl font-black tracking-tighter text-[#0A0A0A]" >
            ADMIN DASHBOARD
          </h1>
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="view-site-link" className="text-sm text-[#8A8A8A] hover:text-[#E63946]" >
              View Site
            </Link>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="flex items-center gap-2 px-4 py-2 border border-[#E5E5E5] hover:border-[#E63946] hover:text-[#E63946] transition-colors"
              
            >
              <LogOut size={16} />
              LOGOUT
            </button>
          </div>
        </div>
      </div>
      
      <div className="max-w-[1600px] mx-auto p-6">
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('articles')}
            data-testid="tab-articles"
            className={`flex items-center gap-2 px-6 py-3 font-bold text-sm tracking-[0.2em] uppercase transition-colors ${
              activeTab === 'articles'
                ? 'bg-[#E63946] text-white'
                : 'bg-white border border-[#E5E5E5] text-[#8A8A8A] hover:border-[#E63946]'
            }`}
            
          >
            <FileText size={16} />
            ARTICLES
          </button>
          <button
            onClick={() => setActiveTab('videos')}
            data-testid="tab-videos"
            className={`flex items-center gap-2 px-6 py-3 font-bold text-sm tracking-[0.2em] uppercase transition-colors ${
              activeTab === 'videos'
                ? 'bg-[#E63946] text-white'
                : 'bg-white border border-[#E5E5E5] text-[#8A8A8A] hover:border-[#E63946]'
            }`}
            
          >
            <Video size={16} />
            VIDEOS
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            data-testid="tab-settings"
            className={`flex items-center gap-2 px-6 py-3 font-bold text-sm tracking-[0.2em] uppercase transition-colors ${
              activeTab === 'settings'
                ? 'bg-[#E63946] text-white'
                : 'bg-white border border-[#E5E5E5] text-[#8A8A8A] hover:border-[#E63946]'
            }`}
            
          >
            <Settings size={16} />
            SETTINGS
          </button>
        </div>
        
        {activeTab === 'articles' && (
          <div className="bg-white border border-[#E5E5E5] p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold tracking-tight text-[#0A0A0A]" >
                Manage Articles
              </h2>
              <Link
                to="/admin/articles/create"
                data-testid="create-article-button"
                className="flex items-center gap-2 px-4 py-2 bg-[#E63946] text-white font-bold text-sm tracking-[0.2em] uppercase hover:bg-[#d32f3e] transition-colors"
                
              >
                <Plus size={16} />
                NEW ARTICLE
              </Link>
            </div>
            
            <div className="space-y-4" data-testid="articles-list">
              {articles.map((article, idx) => (
                <div key={article.id} className="border border-[#E5E5E5] p-4 hover:border-[#E63946] transition-colors" data-testid={`article-item-${idx}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A] mb-2" >
                        {article.category} {article.featured && '• FEATURED'}
                      </div>
                      <h3 className="text-xl font-semibold text-[#0A0A0A] mb-2" >
                        {article.title}
                      </h3>
                      <p className="text-sm text-[#525252] mb-2" >
                        {getArticleSnippet(article.content, 100)}
                      </p>
                      <div className="text-xs text-[#8A8A8A]" >
                        By {article.author} • {new Date(article.published_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Link
                        to={`/admin/articles/edit/${article.id}`}
                        data-testid={`edit-article-${idx}`}
                        className="p-2 border border-[#E5E5E5] hover:border-[#E63946] hover:text-[#E63946] transition-colors"
                      >
                        <Edit size={18} />
                      </Link>
                      <button
                        onClick={() => handleDeleteArticle(article.id)}
                        data-testid={`delete-article-${idx}`}
                        className="p-2 border border-[#E5E5E5] hover:border-[#E63946] hover:text-[#E63946] transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {articles.length === 0 && (
                <div className="text-center py-12 text-[#8A8A8A]" >
                  No articles yet. Create your first article!
                </div>
              )}
            </div>
          </div>
        )}
        
        {activeTab === 'videos' && (
          <div className="bg-white border border-[#E5E5E5] p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold tracking-tight text-[#0A0A0A]" >
                Manage Videos
              </h2>
              <Link
                to="/admin/videos/create"
                data-testid="create-video-button"
                className="flex items-center gap-2 px-4 py-2 bg-[#E63946] text-white font-bold text-sm tracking-[0.2em] uppercase hover:bg-[#d32f3e] transition-colors"
                
              >
                <Plus size={16} />
                NEW VIDEO
              </Link>
            </div>
            
            <div className="space-y-4" data-testid="videos-list">
              {videos.map((video, idx) => (
                <div key={video.id} className="border border-[#E5E5E5] p-4 hover:border-[#E63946] transition-colors" data-testid={`video-item-${idx}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-[#0A0A0A] mb-2" >
                        {video.title}
                      </h3>
                      <p className="text-sm text-[#525252] mb-2" >
                        {video.description}
                      </p>
                      <div className="text-xs text-[#8A8A8A]" >
                        {video.youtube_url}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Link
                        to={`/admin/videos/edit/${video.id}`}
                        data-testid={`edit-video-${idx}`}
                        className="p-2 border border-[#E5E5E5] hover:border-[#E63946] hover:text-[#E63946] transition-colors"
                      >
                        <Edit size={18} />
                      </Link>
                      <button
                        onClick={() => handleDeleteVideo(video.id)}
                        data-testid={`delete-video-${idx}`}
                        className="p-2 border border-[#E5E5E5] hover:border-[#E63946] hover:text-[#E63946] transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {videos.length === 0 && (
                <div className="text-center py-12 text-[#8A8A8A]" >
                  No videos yet. Add your first video!
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6">
            <ChangePasswordPanel />
            <SocialSettingsPanel />
            <AdsSettingsPanel />
            <LegalSettingsPanel />
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboardPage;
